import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { formatDateTimeInViewerTZ, getViewerTimezone } from '../utils/dateTimeUtils';
import { useAuth } from '../contexts/AuthContext';
import { formatBatchStudentName, getBatchStudents, resolveBatchStudentCount } from '../utils/batchDetail';
import StudentHandoverButton from './StudentHandoverButton';
import StudentLeaveUnassignDialog from './StudentLeaveUnassignDialog';

/**
 * Shared assigned-students table (same shape as GET /api/batches/:id → data.students).
 * Used on batch list view dialog, batch detail page, and batch edit form.
 */
export default function BatchAssignedStudentsPanel({
  batch,
  students: studentsProp,
  storedCapacity,
  loading = false,
  canManageAssignments = false,
  onUnassign,
  unassigningAssignmentId = null,
  variant = 'full',
  title = 'Assigned Students',
  sx,
}) {
  const { user } = useAuth();
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const students = studentsProp ?? getBatchStudents(batch);
  const [leaveTarget, setLeaveTarget] = useState(null);
  const count = students.length || resolveBatchStudentCount(batch);
  const stored =
    storedCapacity != null ? storedCapacity : batch?.current_capacity;
  const actual = batch?.actual_student_count ?? count;
  const showCapacityWarning =
    stored != null && actual != null && Number(stored) !== Number(actual);
  const extended = variant === 'full';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3} sx={sx}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  return (
    <Box sx={sx}>
      <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h6">
          {title} ({count})
        </Typography>
        {showCapacityWarning && (
          <Alert severity="warning" sx={{ flex: 1, ml: 2, py: 0 }}>
            Capacity mismatch: stored {stored}, actual {actual}
          </Alert>
        )}
      </Box>
      {students.length === 0 ? (
        <Alert severity="info">No students assigned to this batch yet.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Name</strong>
                </TableCell>
                <TableCell>
                  <strong>Email</strong>
                </TableCell>
                {extended && (
                  <TableCell>
                    <strong>Phone</strong>
                  </TableCell>
                )}
                <TableCell>
                  <strong>Age Group</strong>
                </TableCell>
                <TableCell>
                  <strong>Type</strong>
                </TableCell>
                {extended && (
                  <TableCell>
                    <strong>Assignment Type</strong>
                  </TableCell>
                )}
                <TableCell>
                  <strong>Assigned Date</strong>
                </TableCell>
                {canManageAssignments && (
                  <TableCell align="right">
                    <strong>Actions</strong>
                  </TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {students.map((student) => (
                <TableRow key={student.assignment_id || student.student_id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <StudentHandoverButton
                        studentId={student.student_id}
                        batchId={batch?.id || null}
                      />
                      <Typography variant="body2">{formatBatchStudentName(student)}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {student.student_email || 'N/A'}
                    </Typography>
                  </TableCell>
                  {extended && (
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {student.student_phone || 'N/A'}
                      </Typography>
                    </TableCell>
                  )}
                  <TableCell>
                    <Chip label={student.age_group || '—'} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={student.student_type || '—'}
                      size="small"
                      color={student.student_type === 'normal' ? 'default' : 'secondary'}
                    />
                  </TableCell>
                  {extended && (
                    <TableCell>
                      <Chip
                        label={student.assignment_type || '—'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {formatDateTimeInViewerTZ(student.assignment_date, viewerTimezone) || '—'}
                    </Typography>
                  </TableCell>
                  {canManageAssignments && (
                    <TableCell align="right">
                      {onUnassign ? (
                        <Button
                          size="small"
                          color="error"
                          variant="outlined"
                          onClick={() => setLeaveTarget(student)}
                          disabled={unassigningAssignmentId === student.assignment_id}
                        >
                          {unassigningAssignmentId === student.assignment_id ? (
                            <CircularProgress size={18} />
                          ) : (
                            'Unassign'
                          )}
                        </Button>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <StudentLeaveUnassignDialog
        open={Boolean(leaveTarget)}
        studentName={leaveTarget ? formatBatchStudentName(leaveTarget) : ''}
        batchName={batch?.name || ''}
        submitting={Boolean(leaveTarget && unassigningAssignmentId === leaveTarget.assignment_id)}
        onClose={() => setLeaveTarget(null)}
        onConfirm={async (payload) => {
          await onUnassign(leaveTarget.assignment_id, payload);
          setLeaveTarget(null);
        }}
      />
    </Box>
  );
}
